/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import * as go from 'gojs';

import { LINK_STYLES, TOOLTIP_STYLES, TURN_EDGE_LABEL_STYLES } from '../graph-config';
import { highlightPath } from '../../../helpers/graph';
import { GoMake } from './shared';

/**
 * Link template with hover highlighting
 */
export function createLinkTemplate($: GoMake): go.Link {
  return $(
    go.Link,
    {
      routing: go.Routing.Normal,
      curve: go.Curve.None,
      toShortLength: 4,
      layerName: 'Background',
      selectable: true,
      selectionAdorned: false, // Disable default blue selection adornment
      selectionChanged: (part: go.Part) => {
        const link = part as go.Link;
        const lineShape = link.findObject('LINE') as go.Shape;
        const arrowShape = link.findObject('ARROW') as go.Shape;
        const stepNumberCircle = link.findObject('STEP_CIRCLE') as go.Shape;
        const stepNumberText = link.findObject('STEP_TEXT') as go.TextBlock;

        if (link.isSelected) {
          // Active/Selected state — use error highlight color for ERROR edges
          const isError = link.data?.status === 'ERROR';
          const activeColor = isError ? LINK_STYLES.error.highlightStroke : LINK_STYLES.selected.stroke;
          const circleColor = isError ? LINK_STYLES.error.highlightStroke : LINK_STYLES.selected.stepCircleStroke;
          if (lineShape) lineShape.stroke = activeColor;
          if (arrowShape) {
            arrowShape.stroke = activeColor;
            arrowShape.fill = activeColor;
          }
          if (stepNumberCircle) {
            stepNumberCircle.fill = circleColor;
            stepNumberCircle.stroke = circleColor;
          }
          if (stepNumberText) {
            stepNumberText.stroke = 'white';
          }
        } else {
          // Normal state — respect error status
          const isError = link.data?.status === 'ERROR';
          if (lineShape) lineShape.stroke = isError ? LINK_STYLES.error.stroke : LINK_STYLES.line.stroke;
          if (arrowShape) {
            arrowShape.stroke = isError ? LINK_STYLES.error.stroke : LINK_STYLES.arrow.stroke;
            arrowShape.fill = isError ? LINK_STYLES.error.stroke : LINK_STYLES.arrow.fill;
          }
          if (stepNumberCircle) {
            stepNumberCircle.fill = LINK_STYLES.stepNumber.circleFill;
            stepNumberCircle.stroke = isError ? LINK_STYLES.error.stroke : LINK_STYLES.stepNumber.circleStroke;
          }
          if (stepNumberText) {
            stepNumberText.stroke = LINK_STYLES.stepNumber.textStroke;
          }
        }
      },
      mouseEnter: (_e: go.InputEvent, link: go.GraphObject) => {
        const l = link as go.Link;
        if (!l.isSelected) {
          highlightPath(null, l, true);
        }
      },
      mouseLeave: (_e: go.InputEvent, link: go.GraphObject) => {
        const l = link as go.Link;
        if (!l.isSelected) {
          highlightPath(null, l, false);
        }
      },
      // Tooltip for showing edge_type and id on hover
      // Error state uses a distinct red design; normal state uses the default dark design
      // Links going to the end node do not show a tooltip
      toolTip: $(
        go.Adornment,
        'Auto',
        new go.Binding('visible', 'to', (to: string) => !String(to).startsWith('end:')),
        $(
          go.Shape,
          'RoundedRectangle',
          new go.Binding('fill', 'status', (s: string) =>
            s.toUpperCase() === 'ERROR' ? TOOLTIP_STYLES.error.fill : TOOLTIP_STYLES.background.fill
          ),
          new go.Binding('stroke', 'status', (s: string) =>
            s.toUpperCase() === 'ERROR' ? TOOLTIP_STYLES.error.stroke : TOOLTIP_STYLES.background.stroke
          ),
        ),
        $(
          go.Panel,
          'Vertical',
          {
            margin: TOOLTIP_STYLES.link.panelMargin,
            defaultAlignment: go.Spot.Left,
            minSize: new go.Size(TOOLTIP_STYLES.minWidth, 0),
          },
          new go.Binding('margin', 'status', (s: string) =>
            s === 'ERROR'
              ? TOOLTIP_STYLES.link.errorPanelMargin
              : TOOLTIP_STYLES.link.panelMargin
          ),
          // Error header row: badge icon + "Error" label — only shown for ERROR status
          $(
            go.Panel,
            'Horizontal',
            {
              visible: false,
              defaultAlignment: go.Spot.Center,
              margin: new go.Margin(0, 0, 8, 0),
            },
            new go.Binding('visible', 'status', (s: string) => s === 'ERROR'),
            $(go.Picture, {
              source: '/logos/error-badge-outline.svg',
              width: 16,
              height: 16,
              margin: new go.Margin(0, 4, 0, 0),
            }),
            $(go.TextBlock, {
              text: 'Error',
              font: TOOLTIP_STYLES.error.font,
              stroke: TOOLTIP_STYLES.link.title.stroke,
            }),
          ),
          // Separator line — only shown for ERROR status
          $(
            go.Shape,
            'Rectangle',
            {
              height: 1,
              fill: TOOLTIP_STYLES.error.dividerStroke,
              strokeWidth: 0,
              stretch: go.Stretch.Horizontal,
              visible: false,
              margin: new go.Margin(0, 0, 12, 0),
            },
            new go.Binding('visible', 'status', (s: string) => s === 'ERROR'),
          ),
          // Edge type title
          $(
            go.TextBlock,
            {
              font: TOOLTIP_STYLES.link.title.font,
              stroke: TOOLTIP_STYLES.link.title.stroke,
              margin: TOOLTIP_STYLES.link.title.margin,
            },
            new go.Binding('text', 'edgeType')
          ),
          // Status message / error description — hidden when null, undefined, or empty
          $(
            go.TextBlock,
            {
              font: TOOLTIP_STYLES.link.subtitle.font,
              stroke: TOOLTIP_STYLES.link.subtitle.stroke,
              wrap: go.Wrap.Fit,
              maxSize: TOOLTIP_STYLES.link.subtitle.maxSize,
              visible: false,
            },
            new go.Binding('visible', 'statusMsg', (t) => t !== undefined && t !== '' && t !== null),
            new go.Binding('text', 'statusMsg', (t) => (t !== null ? t : null)),
          ),
        )
      ),
    },
    // Link line (named for easy access)
    $(go.Shape, {
      name: 'LINE',
      strokeWidth: LINK_STYLES.line.strokeWidth,
      stroke: LINK_STYLES.line.stroke,
    },
      new go.Binding('stroke', 'status', (s: string) =>
        s === 'ERROR' ? LINK_STYLES.error.stroke : LINK_STYLES.line.stroke
      )
    ),
    // Arrow (named for easy access)
    $(go.Shape, {
      name: 'ARROW',
      toArrow: LINK_STYLES.arrow.toArrow,
      fill: LINK_STYLES.arrow.fill,
      stroke: LINK_STYLES.arrow.stroke,
    },
      new go.Binding('stroke', 'status', (s: string) =>
        s === 'ERROR' ? LINK_STYLES.error.stroke : LINK_STYLES.arrow.stroke
      ),
      new go.Binding('fill', 'status', (s: string) =>
        s === 'ERROR' ? LINK_STYLES.error.stroke : LINK_STYLES.arrow.fill
      )
    ),
      // Step number - on the line with background matching diagram
    $(
      go.Panel,
      'Auto',
      {
        segmentIndex: NaN,
        segmentFraction: 0.5,
        segmentOffset: new go.Point(0, 0),
        segmentOrientation: go.Orientation.Upright,
        // Hide when stepNumber is not present
        visible: false,
      },
      new go.Binding('visible', 'stepNumber', (s) =>
        s !== undefined && s !== null && s !== ''
      ),
      // Circle background
      $(go.Shape, 'Circle', {
        name: 'STEP_CIRCLE',
        fill: LINK_STYLES.stepNumber.circleFill,
        stroke: LINK_STYLES.stepNumber.circleStroke,
        strokeWidth: LINK_STYLES.stepNumber.circleStrokeWidth,
        minSize: LINK_STYLES.stepNumber.minSize,
      },
        new go.Binding('stroke', 'status', (s: string) =>
          s === 'ERROR' ? LINK_STYLES.error.stroke : LINK_STYLES.stepNumber.circleStroke
        )
      ),
      $(
        go.TextBlock,
        {
          name: 'STEP_TEXT',
          font: LINK_STYLES.stepNumber.textFont,
          stroke: LINK_STYLES.stepNumber.textStroke,
          textAlign: 'center',
        },
        new go.Binding('text', 'stepNumber', (s) =>
          s !== undefined && s !== null ? s.toString() : ''
        )
      )
    ),
    // Optional edge label (e.g. 'Turn') shown beside the line — only when edgeLabel data is set
    $(
      go.Panel,
      'Auto',
      {
        segmentIndex: NaN,
        segmentFraction: 0.5,
        segmentOrientation: go.Orientation.Minus90,
        visible: false,
      },
      new go.Binding('visible', 'edgeLabel', (s) => !!s),
      $(go.Shape, 'RoundedRectangle', {
        fill: LINK_STYLES.stepNumber.circleFill,
        stroke: null,
        strokeWidth: 0,
      }),
      $(
        go.TextBlock,
        {
          font: TURN_EDGE_LABEL_STYLES.text.font,
          stroke: TURN_EDGE_LABEL_STYLES.text.stroke,
          textAlign: 'center',
        },
        new go.Binding('text', 'edgeLabel', (s) => s ?? '')
      )
    ),
  );
}